import React from 'react';
import styles from '../AppointmentManagement/AppointmentManagement.module.css';
import {Appointment} from 'externalAPIs/insurance-eligibility';
import {formatAppointmentStartTime, getAppointmentStatus, STATUS_TYPES} from '../utils';
import {CheckmarkIcon, ExclamationIcon} from '../icons';

const RedTriangleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 5L14.8 14.5C15.0667 15 14.8333 15.3333 14.3333 15.3333H5.66667C5.16667 15.3333 4.93333 15 5.2 14.5L10 5Z"
      fill="#F04E52"
    />
  </svg>
);

const EmptyCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="9" stroke="var(--color-web-purple2)" strokeWidth="2" />
  </svg>
);

const EmptyCircleIconGray = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="9" stroke="var(--color-gray3, #b3b3b3)" strokeWidth="2" />
  </svg>
);

const BulletIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{width: '12px', height: '12px', marginTop: '4px', flexShrink: 0}}
  >
    <circle cx="10" cy="10" r="6" fill="var(--color-web-purple1)" />
  </svg>
);

interface ActiveAppointmentStatusProps {
  appointment: Appointment;
  tags: string[];
  cardUploadStatus: string;
}

export const ActiveAppointmentStatus: React.FC<ActiveAppointmentStatusProps> = ({
  appointment,
  tags,
  cardUploadStatus,
}) => {
  const formattedStartTime = formatAppointmentStartTime(appointment?.startTime);
  const status = getAppointmentStatus(appointment, tags, cardUploadStatus);
  const statusDisplay = status.charAt(0).toUpperCase() + status.slice(1);

  const isPending = status === STATUS_TYPES.PENDING;
  const statusBadgeClass = isPending ? `${styles.statusBadge} ${styles.statusBadgePending}` : styles.statusBadge;

  return (
    <div className={styles.statusContent}>
      {formattedStartTime ? (
        <div className={styles.appointmentTimeRow}>
          <div className={styles.appointmentStartTime}>{formattedStartTime}</div>
          <div className={statusBadgeClass}>
            {isPending ? <ExclamationIcon /> : <CheckmarkIcon />}
            <span>{statusDisplay}</span>
          </div>
        </div>
      ) : (
        <div className={statusBadgeClass}>
          {isPending ? <ExclamationIcon /> : <CheckmarkIcon />}
          <span>{statusDisplay}</span>
        </div>
      )}
      {status === STATUS_TYPES.UPLOADED ? (
        <>
          <div className={styles.statusItem}>
            <CheckmarkIcon />
            <div className={styles.statusText}>
              <strong>Expert found:</strong> We&apos;ve matched you with an expert based on your sleep challenges.
              Reschedule 24 hours before your session to avoid a $50 no-show fee.
            </div>
          </div>
          <div className={styles.statusItem}>
            <CheckmarkIcon />
            <div className={styles.statusText}>
              <strong>Upload insurance card:</strong> Please upload photos of your card at the Insurance button above
              within 24 hours to verify benefits
            </div>
          </div>
          <div className={styles.statusItem}>
            <EmptyCircleIcon />
            <div className={styles.statusText}>
              <strong>Verifying benefits:</strong> We&apos;ll work with your insurance and reach out with your
              out-of-pocket costs.
            </div>
          </div>
        </>
      ) : status === STATUS_TYPES.ELIGIBLE ? (
        <>
          <div className={styles.statusItem}>
            <BulletIcon />
            <div className={styles.statusText}>
              Comprehensive sleep evaluation to find root causes with tailored advice
            </div>
          </div>
          <div className={styles.statusItem}>
            <BulletIcon />
            <div className={styles.statusText}>
              Cancel or reschedule 24 hours before your session to avoid a $50 no-show fee
            </div>
          </div>
        </>
      ) : status === STATUS_TYPES.PENDING ? (
        <>
          <div className={styles.statusItem}>
            <CheckmarkIcon />
            <div className={styles.statusText}>
              <strong>Expert found:</strong> We&apos;ve matched you with an expert based on your sleep challenges.
              Reschedule 24 hours before your session to avoid a $50 no-show fee.
            </div>
          </div>
          <div className={styles.statusItem}>
            <RedTriangleIcon />
            <div className={styles.statusText}>
              <strong>Upload insurance card:</strong> Please upload photos of your card at the Insurance button above
              within 24 hours to verify benefits
            </div>
          </div>
          <div className={styles.statusItem}>
            <EmptyCircleIconGray />
            <div className={`${styles.statusText} ${styles.statusTextGray}`}>
              <strong>Verifying benefits:</strong> We&apos;ll work with your insurance and reach out with your
              out-of-pocket costs.
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};
